/**
 * Composable per Purchase Orders autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { BaseAutocompleteOptions } from '@/lib/autocompletes';
import type { PurchaseOrderAutocompleteResult } from '@/lib/models/purchaseOrder';
import { getPurchaseOrdersAutocomplete } from '@/lib/autocompletes/purchaseOrders';

export function usePurchaseOrdersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const purchaseOrders = ref<PurchaseOrderAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchPurchaseOrders(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getPurchaseOrdersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					purchaseOrders.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR usePurchaseOrdersAutocomplete.searchPurchaseOrders:', err);
				error('Errore durante il caricamento delle commesse! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					purchaseOrders.value = [];
				}
			}
		}, 'searchPurchaseOrders');
	}

	function clearPurchaseOrders(): void {
		purchaseOrders.value = [];
	}

	return {
		purchaseOrders: readonly(purchaseOrders),
		searchPurchaseOrders,
		clearPurchaseOrders,
	};
}
